/**
 * One of a domain's own contacts (admin, tech or billing) takes a subject's current details:
 * this domain only; other domains cloned from the same subject keep theirs. Only individuals are
 * offered (owner decision 2026-10-06: an organisation's admin, tech and billing are people).
 */

import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { api, type DomainDetail } from '../../../api';
import type { ContactRole } from '../../../api/areas/core';
import { Callout, Field } from '../../../components/blocks';
import { useWrite, WriteError } from '../../../components/Write';
import { useApi } from '../../../lib/useApi';
import { useVocab } from '../refs';

const selectClass =
  'h-9 w-full min-w-0 rounded-md border border-input bg-transparent px-3 py-1 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 dark:bg-input/30';

export function EditContactDialog({
  detail,
  role,
  onClose,
  onChanged,
}: {
  detail: DomainDetail;
  role: ContactRole;
  /** The contact's subject now (not offered again: admin, tech and billing are individuals). */
  current: string | null;
  onClose: () => void;
  onChanged: () => void;
}) {
  const { t } = useTranslation('domains');
  const vocab = useVocab();
  const d = detail.domain;
  const subjects = useApi((signal) => api.listSubjects({ customer_id: d.customer_id, subject_type: 'INDIVIDUAL', limit: 100 }, { signal }), [d.customer_id]);
  const [subject, setSubject] = useState('');
  const [reason, setReason] = useState('');
  const w = useWrite((b: { subject_id: string; reason: string }, opts) => api.editDomainContact(d.id, role, b, opts));
  const done = w.state.phase === 'done';
  return (
    <Dialog open onOpenChange={(open) => (open ? null : onClose())}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>{t('contacts.editTitle', { role: vocab('role', role) })}</DialogTitle>
          <DialogDescription>{t('contacts.editDescription', { domain: d.unicode_name })}</DialogDescription>
        </DialogHeader>
        <form
          className="grid gap-4"
          onSubmit={(e) => {
            e.preventDefault();
            void w.run({ subject_id: subject, reason }).then((r) => r && onChanged());
          }}
        >
          <Field label={t('contacts.editSubject')} hint={t('contacts.editSubjectHint')}>
            <select className={selectClass} value={subject} onChange={(e) => setSubject(e.target.value)} required disabled={done}>
              <option value="">{subjects.status === 'loading' ? t('actions.loading') : t('actions.choose')}</option>
              {subjects.status === 'success'
                ? subjects.data.items.map((s) => (
                    <option key={s.id} value={s.id}>
                      {s.legal_name ?? s.id}
                    </option>
                  ))
                : null}
            </select>
          </Field>
          <Field label={t('actions.reason')} hint={t('actions.reasonHint')}>
            <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} maxLength={500} required disabled={done} />
          </Field>
          {w.state.phase === 'error' ? <WriteError error={w.state.error} /> : null}
          {done ? <Callout tone="ok">{t('contacts.editStarted')}</Callout> : null}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>
              {t('actions.close')}
            </Button>
            <Button type="submit" disabled={w.busy || done || !subject || !reason.trim()}>
              {t('contacts.editSubmit')}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
